/* eslint-disable complexity */
/* eslint-disable max-lines */
import { useEffect, useState, useMemo, useRef } from 'preact/hooks';
import { createContext } from 'preact';
import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';

import { useInView } from 'react-intersection-observer';

import { useTranslation } from 'react-i18next';
import { InteractionEvent, getAnalyticsContext } from '@playbook/sdk-helpers';

import PollHeader from '../components/Polling/pollHeader.component';
import PollVoteScreen from '../components/Polling/pollVoteScreen.component';
import PollScheduledScreen from '../components/Polling/pollScheduledScreen.component';
import PollResultsScreen from '../components/Polling/pollResultsScreen.component';
import PollVoteCastScreen from '../components/Polling/pollVoteCastScreen.component';
import PollHero from '../components/Polling/pollHero.component';
import PollSponsor from '../components/Polling/pollSponsor.component';

import classNames from 'classnames';
import styleSheet from '../components/Polling/poll.module.scss';
import { sendView } from '../helpers/analytics';
import SubmitButton from '../components/shared-components/button/SubmitButton';
import { DataCaptureFormComponent as Form } from 'playbook-data-capture-sdk-web';

import { getOrCreateEncodedUUID } from '../helpers/user-meta';
import { getClientHost } from '../helpers/client';

import { sendCustomEventVoted } from '../helpers/customEvents';
import { getMultiSelectOffset } from '../helpers/formOffset';

import {
    isOpen,
    isScheduled,
    getUserVote,
    getTotalVotes,
    RESULTS_VISIBILITY,
    pollHasOptionImages,
    sortPollOptions,
    getWinningVotes
} from './potg.helpers';

import {
    getAuthHeader,
    checkForSignIn,
    getUserIdFromAuthHeader
} from '../helpers/authentication';

export const JITTER_TIMEOUT = 2000;
export const PollContext = createContext({});

const ONE_THOUSAND = 1000;

/**
 * Component for showing a potg
 *
 * @param {object} props potg props
 * @param {object} props.data POTG data
 * @param {boolean} props.isPreview poll embed is a preview
 * @param {Function} props.onPreviewEditChange callback function when contenteditable data changes
 * @param props.isMockPoll
 * @param props.refetch
 * @param props.embeddedFormData
 * @param props.colourOverrides
 * @param props.container
 * @returns {Function} <PlayerOfTheGameComponent />
 */
function PlayerOfTheGameComponent({
    data,
    container,
    refetch,
    embeddedFormData,
    colourOverrides,
    isPreview = false,
    onPreviewEditChange,
    isMockPoll = false
}) {
    const { t } = useTranslation();

    const [poll, setPoll] = useState(data);

    const [isSignedIn, setIsSignedIn] = useState(false);
    const isGated = poll?.gated;
    const authHeader = getAuthHeader();

    const [selectedOptions, setSelectedOptions] = useState([]);

    const [pollUserAnswer, setPollUserAnswer] = useState(
        getUserVote(poll.poll_id)
    );
    const [startEmbedAnimation, setStartEmbedAnimation] = useState(false);
    const [startBarsAnimation, setStartBarsAnimation] = useState(false);
    const [submittingVote, setSubmittingVote] = useState(false);
    const [dataCaptureFormSubmitted, setDataCaptureFormSubmitted] =
        useState(false);
    const [goToPollResult, setGoToPollResult] = useState(false);

    const interactionEventRef = useRef(null);

    const pollOpen = isOpen(poll);
    const pollScheduled = isScheduled(poll);
    const totalVotes = getTotalVotes(poll);
    const winningVotes = getWinningVotes(poll);
    const hasOptionImages = pollHasOptionImages(poll);

    const hasHeroImg = poll?.hero_image_url || poll?.hero_external_image_url;
    const hasSponsorImg =
        poll?.sponsor_image_url || poll?.sponsor_external_image_url;

    const isDraft = poll?.state === 'draft';

    const [forceForm, setForceForm] = useState(false);
    const [formComplete, setFormComplete] = useState(false);

    const styles = styleSheet.locals || {};
    const THREE_SECONDS = 3000;

    const analyticsContext = useMemo(
        () => getAnalyticsContext(container),
        [container]
    );

    const showVotesCounter = totalVotes > ONE_THOUSAND;

    const { ref } = useInView({
        triggerOnce: true,
        threshold: 0.1,
        onChange: (inView) => {
            if (inView) {
                setStartEmbedAnimation(true);
                setStartBarsAnimation(true);

                if (!isMockPoll && !isPreview) {
                    // Dont register a view when user cannot use the poll
                    if (isGated && !authHeader) {
                        return;
                    }
                    sendView('POLL', poll?.poll_id, isGated, authHeader)
                        .then(() => {
                            interactionEventRef.current?.send('POLL_VIEWED');
                        })
                        .catch((error) => {
                            // Handle the error here
                            console.error('Error sending view:', error);
                        });
                }
            }
        }
    });

    const applyPollSettings = () => {
        const pollSettingsAttr =
            container.previousElementSibling?.getAttribute('data-fek-settings');
        if (pollSettingsAttr) {
            const settings = JSON.parse(pollSettingsAttr);

            if (settings.primaryColour) {
                container.style.setProperty(
                    '--fek-primary',
                    settings.primaryColour
                );
            }

            if (settings.primaryColourRgb) {
                container.style.setProperty(
                    '--fek-primary-rgb',
                    settings.primaryColourRgb
                );
            }
        } else if (colourOverrides?.hex && colourOverrides?.rgb) {
            container?.style?.setProperty('--fek-primary', colourOverrides.hex);

            container?.style?.setProperty(
                '--fek-primary-rgb',
                colourOverrides.rgb
            );
        }
    };

    const submitPollAnswer = (
        pollId,
        questionId,
        answerIdList,
        isGated,
        authHeader
    ) => {
        const userId = isMockPoll
            ? 'MmNkZDQ2NTAtZDA5NC00MmQ4LTkxNDgtODhiYWZlNTg3MWJh'
            : getOrCreateEncodedUUID();

        const pollEndpoint = isGated
            ? `https://${getClientHost()}${
                  process.env.API_PATH
              }/gated/polls/${pollId}/questions/${questionId}/answers`
            : `https://${getClientHost()}${
                  process.env.API_PATH
              }/polls/${pollId}/questions/${questionId}/answers`;

        const data = {
            answer_options: answerIdList.map((answer) => ({
                option_id: answer
            }))
        };

        const body = JSON.stringify(data);

        const headersConfig = {
            'Content-Type': 'application/json'
        };

        if (isGated && authHeader) {
            headersConfig.Authorization = authHeader;
        }

        if (!isGated && userId) {
            headersConfig['X-User-Id'] = userId;
        }

        return fetch(pollEndpoint, {
            method: 'POST',
            headers: headersConfig,
            body
        });
    };

    const onVoteSubmit = async () => {
        if (isPreview) {
            return;
        }

        if (selectedOptions.length > 0) {
            setSubmittingVote(true);
            setStartBarsAnimation(false);

            const selectedOptionsId = selectedOptions.map(
                (option) => option.option_id
            );

            const submissionResponse = await submitPollAnswer(
                poll.poll_id,
                poll.questions[0].question_id,
                selectedOptionsId,
                isGated,
                authHeader
            );

            setTimeout(async () => {
                const updatedPoll = await refetch();
                sortPollOptions(updatedPoll);

                setPoll(updatedPoll);
                setPollUserAnswer(selectedOptionsId);
                setSubmittingVote(false);
                setTimeout(() => setStartBarsAnimation(true), 0);

                localStorage.setItem(poll.poll_id, selectedOptionsId);

                sendCustomEventVoted(
                    updatedPoll,
                    selectedOptions,
                    submissionResponse
                );

                interactionEventRef.current?.send('POLL_SUBMITTED');

                if (!isInstantResults && embeddedFormData) {
                    setForceForm(true);
                }
            }, JITTER_TIMEOUT);
        } else {
            console.warn('NO OPTION SELECTED!');
        }
    };

    const onCountdownEnd = () => {
        setTimeout(async () => {
            const updatedPoll = await refetch();
            setPoll(updatedPoll);
        }, THREE_SECONDS);
    };

    const isInstantResults =
        poll.questions[0]?.results_visibility ===
        RESULTS_VISIBILITY.AFTER_SUBMISSION;

    const pollWidth = poll.settings?.width || '';
    const transitionStyles = {
        entered: { opacity: 1, transform: 'rotate(0deg)' }
    };
    const transitionDelay = 100;
    const transitionLength = 300;
    const submitTransitionDelay =
        (poll.questions?.[0]?.options?.length || 0) * transitionDelay +
        transitionLength;

    const showPollOptions =
        isGated && !isSignedIn && !isDraft ? true : !pollUserAnswer && pollOpen;
    const hasResults = poll.questions[0]?.options.length > 0;
    const resultsVisibility =
        poll.questions[0]?.results_visibility ||
        RESULTS_VISIBILITY.AFTER_SUBMISSION;
    const arePollResultsReady =
        (pollOpen &&
            pollUserAnswer &&
            resultsVisibility === RESULTS_VISIBILITY.AFTER_SUBMISSION) ||
        (!pollOpen && hasResults && !pollScheduled);

    const showScheduledScreen = !pollOpen && pollScheduled;

    const showDataCaptureForm =
        arePollResultsReady &&
        embeddedFormData?.state === 'open' &&
        !goToPollResult;

    const formOffsetPixels = getMultiSelectOffset(embeddedFormData);

    const showResultsScreen =
        isGated && !isSignedIn
            ? arePollResultsReady && !showDataCaptureForm && isSignedIn
            : arePollResultsReady && !showDataCaptureForm;

    let showVoteCastScreen = false;
    switch (resultsVisibility) {
        case RESULTS_VISIBILITY.AFTER_SUBMISSION:
            showVoteCastScreen = false;
            break;
        case RESULTS_VISIBILITY.AFTER_CLOSING:
            showVoteCastScreen = pollOpen;
            break;
        case RESULTS_VISIBILITY.NEVER:
            showVoteCastScreen = true;
            break;
        default:
    }

    // Do not show vote case if form is being triggered
    if (forceForm) {
        showVoteCastScreen = false;
    }

    // allows us to ensure a user will always get a form for the after close
    // and never polls - even if page refresh
    if (
        showVoteCastScreen &&
        pollUserAnswer &&
        embeddedFormData &&
        !formComplete
    ) {
        setForceForm(true);
    }

    useEffect(() => {
        setIsSignedIn(checkForSignIn());
    }, []);

    // Initialize InteractionEvent for analytics
    useEffect(() => {
        let userId = null;

        if (isGated && authHeader) {
            userId = getUserIdFromAuthHeader(authHeader);
        } else if (!isGated) {
            userId = getOrCreateEncodedUUID();
        }

        const interactionEvent = new InteractionEvent({
            activationId: poll?.poll_id,
            userId,
            type: 'POTG',
            isLoggedIn: !!authHeader,
            title: poll?.text,
            context: analyticsContext
        });

        interactionEventRef.current = interactionEvent;

        if (typeof globalThis !== 'undefined') {
            globalThis.PLAYBOOK_CONFIG = {
                ...globalThis.PLAYBOOK_CONFIG,
                interactionEvent
            };
        }
    }, [poll?.poll_id, poll?.text, isGated, authHeader, analyticsContext]);

    useEffect(() => {
        applyPollSettings();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setPoll(data);
    }, [data]);

    useEffect(() => {
        if (isPreview && poll.previewVoted) {
            setPollUserAnswer(poll.questions[0].options[1].option_id);
        } else if (isPreview) {
            setPollUserAnswer(getUserVote(poll.poll_id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPreview, poll.previewVoted]);

    const resultCallback = () => {
        setGoToPollResult(true);
        setForceForm(false);
        setFormComplete(true);
    };

    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(
        () => ({
            poll,
            selectedOptions,
            setSelectedOptions,
            pollOpen,
            startEmbedAnimation,
            submittingVote,
            transitionStyles,
            submitTransitionDelay,
            showVotesCounter,
            onVoteSubmit,
            totalVotes,
            onCountdownEnd,
            isPreview,
            onPreviewEditChange,
            hasOptionImages,
            pollUserAnswer,
            setPollUserAnswer,
            startBarsAnimation,
            isSignedIn,
            setIsSignedIn,
            authHeader,
            isGated,
            winningVotes,
            interactionEvent: interactionEventRef.current
        }),
        [
            poll,
            selectedOptions,
            setSelectedOptions,
            pollOpen,
            startEmbedAnimation,
            submittingVote,
            transitionStyles,
            submitTransitionDelay,
            showVotesCounter,
            onVoteSubmit,
            totalVotes,
            onCountdownEnd,
            isPreview,
            onPreviewEditChange,
            hasOptionImages,
            pollUserAnswer,
            setPollUserAnswer,
            startBarsAnimation,
            isSignedIn,
            setIsSignedIn,
            authHeader,
            isGated,
            winningVotes
        ]
    );

    return (
        <PollContext.Provider value={contextValue}>
            <style>{styleSheet.toString()}</style>
            <Transition nodeRef={ref} in={startEmbedAnimation}>
                {(transitionState) => (
                    <div
                        part="potg-root"
                        data-cy="poll-root"
                        ref={ref}
                        className={classNames(styles.poll, {
                            [styles.pollScheduled]: pollScheduled
                        })}
                        style={{
                            maxWidth: pollWidth,
                            ...transitionStyles[transitionState]
                        }}
                    >
                        {hasHeroImg && <PollHero poll={poll} />}

                        <div part="potg-content" className={styles.pollContent}>
                            <PollHeader
                                poll={poll}
                                pollScheduled={pollScheduled}
                                showVoteCastScreen={showVoteCastScreen}
                                showPollOptions={showPollOptions}
                            />

                            <div part="potg-body" className={styles.pollBody}>
                                {showScheduledScreen && (
                                    <PollScheduledScreen
                                        poll={poll}
                                        pollScheduled={pollScheduled}
                                        onCountdownEnd={onCountdownEnd}
                                    />
                                )}

                                {showPollOptions && !showScheduledScreen && (
                                    <>
                                        <PollVoteScreen
                                            transitionState={transitionState}
                                        />
                                    </>
                                )}

                                {(showDataCaptureForm || forceForm) && (
                                    <div
                                        style={
                                            formOffsetPixels
                                                ? {
                                                      paddingBottom:
                                                          formOffsetPixels
                                                  }
                                                : {}
                                        }
                                    >
                                        <Form
                                            formData={embeddedFormData}
                                            submittedCallback={() => {
                                                setDataCaptureFormSubmitted(
                                                    true
                                                );
                                            }}
                                            pollId={poll.poll_id}
                                            analyticsContext={analyticsContext}
                                        />

                                        {dataCaptureFormSubmitted && (
                                            <SubmitButton
                                                onSubmit={resultCallback}
                                                buttonText={
                                                    !isInstantResults &&
                                                    !arePollResultsReady
                                                        ? t(
                                                              'button.viewPollVote'
                                                          )
                                                        : t(
                                                              'button.viewPollResults'
                                                          )
                                                }
                                                title="View poll results"
                                            />
                                        )}
                                    </div>
                                )}

                                {showResultsScreen && !showVoteCastScreen && (
                                    <PollResultsScreen
                                        transitionDelay={submitTransitionDelay}
                                        transitionState={transitionState}
                                    />
                                )}

                                {showVoteCastScreen && pollUserAnswer && (
                                    <PollVoteCastScreen
                                        resultsVisibility={resultsVisibility}
                                        transitionDelay={submitTransitionDelay}
                                        transitionState={transitionState}
                                    />
                                )}

                                {!pollOpen && !pollScheduled && (
                                    <p
                                        part="potg-closed"
                                        data-testid="closed-message"
                                        className={styles.pollClosed}
                                    >
                                        {t('polling.nowClosed')}
                                    </p>
                                )}
                            </div>

                            {hasSponsorImg && <PollSponsor poll={poll} />}
                        </div>
                    </div>
                )}
            </Transition>
        </PollContext.Provider>
    );
}

PlayerOfTheGameComponent.propTypes = {
    /**
     * Player of the game poll data
     */
    data: PropTypes.object.isRequired,
    /**
     * Container element for the poll
     */
    container: PropTypes.object.isRequired,
    /**
     * Function to refetch the poll data
     */
    refetch: PropTypes.func.isRequired,
    /**
     * Embedded form data
     */
    embeddedFormData: PropTypes.object,
    /**
     * Colour overrides for the poll
     */
    colourOverrides: PropTypes.object,
    /**
     * Whether the poll embed is a preview
     */
    isPreview: PropTypes.bool,
    /**
     * Callback function when contenteditable data changes
     */
    onPreviewEditChange: PropTypes.func,
    /**
     * Whether the poll is a mock poll
     */
    isMockPoll: PropTypes.bool
};

export default PlayerOfTheGameComponent;
